import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowLeft,
  Glasses,
  Hand,
  Move3D,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  Info,
  X,
  Gamepad2,
  Maximize2,
  Camera,
  Eye,
} from 'lucide-react';
import { VREngine } from '@/lib/vr-engine';
import { useUIStore } from '@/stores/ui-store';
import Button from '@/components/ui/Button';

export default function VRViewer() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<VREngine | null>(null);

  const { arModel, setARActive, setARModel } = useUIStore();

  const [isLoading, setIsLoading] = useState(true);
  const [isInVR, setIsInVR] = useState(false);
  const [isInAR, setIsInAR] = useState(false);
  const [vrSupported, setVRSupported] = useState(false);
  const [arSupported, setARSupported] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [modelLoaded, setModelLoaded] = useState(false);
  const [isGrabbing, setIsGrabbing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize VR Engine
  useEffect(() => {
    if (!canvasRef.current) return;

    const engine = new VREngine({
      canvas: canvasRef.current,
      onReady: () => {
        setIsLoading(false);
        checkXRSupport();
      },
      onSessionStart: () => {
        // Check what mode we entered
        if (engine.currentMode === 'ar') {
          setIsInAR(true);
          setIsInVR(false);
        } else {
          setIsInVR(true);
          setIsInAR(false);
        }
      },
      onSessionEnd: () => {
        setIsInVR(false);
        setIsInAR(false);
      },
      onModelGrabbed: () => {
        setIsGrabbing(true);
      },
      onModelReleased: () => {
        setIsGrabbing(false);
      },
      onError: (err) => {
        console.error('[VR] Error:', err);
        setError(err.message);
        setIsLoading(false);
      },
    });

    engineRef.current = engine;
    engine.init();

    return () => {
      engine.dispose();
    };
  }, []);

  // Check WebXR VR and AR support
  const checkXRSupport = async () => {
    if (navigator.xr) {
      // Check VR support
      const vrOk = await navigator.xr.isSessionSupported('immersive-vr');
      setVRSupported(vrOk);

      // Check AR/MR support (passthrough on Quest 3/3S)
      try {
        const arOk = await navigator.xr.isSessionSupported('immersive-ar');
        setARSupported(arOk);
        console.log('[VRViewer] AR/MR supported:', arOk);
      } catch {
        setARSupported(false);
      }
    }
  };

  // Load model when arModel changes
  useEffect(() => {
    if (!engineRef.current || !arModel) return;

    setModelLoaded(false);

    engineRef.current.loadModel({
      url: arModel.modelUrl,
      scale: 1, // Let the engine auto-scale to optimal size
      position: { x: 0, y: 0, z: 2 }, // Position in front, engine will ground it
    }).then(() => {
      setModelLoaded(true);
    });
  }, [arModel]);

  const handleClose = () => {
    setARActive(false);
    setARModel(null);
  };

  const handleEnterVR = async () => {
    if (!engineRef.current) return;

    try {
      await engineRef.current.enterVR();
    } catch (err) {
      console.error('[VR] Failed to enter VR:', err);
      setError('Failed to enter VR mode');
    }
  };

  const handleExitVR = () => {
    engineRef.current?.exitVR();
  };

  const handleEnterAR = async () => {
    if (!engineRef.current) return;

    try {
      await engineRef.current.enterAR();
    } catch (err) {
      console.error('[VR] Failed to enter AR/MR mode:', err);
      setError('Failed to enter Mixed Reality mode');
    }
  };

  const handleScaleUp = useCallback(() => {
    if (!engineRef.current) return;
    const engine = engineRef.current as VREngine & { model?: { scaling: { x: number } } };
    if (engine.model) {
      const newScale = Math.min(5, engine.model.scaling.x * 1.2);
      engineRef.current.setModelScale(newScale);
    }
  }, []);

  const handleScaleDown = useCallback(() => {
    if (!engineRef.current) return;
    const engine = engineRef.current as VREngine & { model?: { scaling: { x: number } } };
    if (engine.model) {
      const newScale = Math.max(0.1, engine.model.scaling.x * 0.8);
      engineRef.current.setModelScale(newScale);
    }
  }, []);

  const handleRotate = useCallback(() => {
    if (!engineRef.current) return;
    const engine = engineRef.current as VREngine & { model?: { rotation: { y: number } } };
    if (engine.model) {
      engineRef.current.setModelRotation(engine.model.rotation.y + Math.PI / 4);
    }
  }, []);

  const handleResetPosition = useCallback(() => {
    engineRef.current?.resetModel();
  }, []);

  return (
    <div className="fixed inset-0 bg-black z-[200]">
      {/* VR Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ touchAction: 'none' }}
      />

      {/* Loading Overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-background flex flex-col items-center justify-center z-10"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              className="w-16 h-16 rounded-full border-4 border-accent-blue/20 border-t-accent-blue mb-4"
            />
            <p className="text-white/70">Loading VR Environment...</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Overlay */}
      {error && (
        <div className="absolute inset-0 bg-background/90 flex flex-col items-center justify-center z-10 p-6">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold mb-2">VR Error</h2>
          <p className="text-white/70 text-center mb-6">{error}</p>
          <Button onClick={handleClose}>Go Back</Button>
        </div>
      )}

      {/* Top Bar */}
      {!isLoading && !isInVR && (
        <div className="absolute top-0 left-0 right-0 p-4 pt-12 flex justify-between items-center safe-area-top z-20">
          <button
            onClick={handleClose}
            className="w-11 h-11 rounded-full glass-dark flex items-center justify-center"
          >
            <ArrowLeft size={22} />
          </button>

          <div className="flex items-center gap-2">
            {arSupported && (
              <div className="px-3 py-1.5 rounded-full bg-purple-500/20 text-purple-400 text-xs font-medium flex items-center gap-1.5">
                <Camera size={14} />
                MR Ready
              </div>
            )}
            {vrSupported ? (
              <div className="px-3 py-1.5 rounded-full bg-green-500/20 text-green-400 text-xs font-medium flex items-center gap-1.5">
                <Glasses size={14} />
                VR Ready
              </div>
            ) : (
              <div className="px-3 py-1.5 rounded-full bg-yellow-500/20 text-yellow-400 text-xs font-medium flex items-center gap-1.5">
                <Eye size={14} />
                3D Preview
              </div>
            )}
          </div>

          <button
            onClick={() => setShowHelp(true)}
            className="w-11 h-11 rounded-full glass-dark flex items-center justify-center"
          >
            <Info size={20} />
          </button>
        </div>
      )}

      {/* Model Info */}
      {!isLoading && !isInVR && arModel && (
        <div className="absolute top-28 left-4 right-4 z-20">
          <div className="glass-dark rounded-2xl p-4">
            <h2 className="font-bold text-lg truncate">{arModel.name}</h2>
            {arModel.description && (
              <p className="text-white/50 text-sm mt-1 line-clamp-2">{arModel.description}</p>
            )}
          </div>
        </div>
      )}

      {/* Grab Indicator */}
      <AnimatePresence>
        {isGrabbing && isInVR && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20"
          >
            <div className="glass-dark rounded-2xl px-6 py-3 flex items-center gap-3">
              <Hand size={24} className="text-accent-blue" />
              <span className="font-medium">Grabbing Model</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Controls (when not in VR) */}
      {!isLoading && !isInVR && modelLoaded && (
        <div className="absolute bottom-32 left-4 right-4 z-20">
          <div className="glass-dark rounded-2xl p-4">
            <div className="flex justify-center gap-3 mb-4">
              <button
                onClick={handleScaleDown}
                className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center active:bg-white/20"
              >
                <ZoomOut size={20} />
              </button>
              <button
                onClick={handleRotate}
                className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center active:bg-white/20"
              >
                <RotateCcw size={20} />
              </button>
              <button
                onClick={handleScaleUp}
                className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center active:bg-white/20"
              >
                <ZoomIn size={20} />
              </button>
              <button
                onClick={handleResetPosition}
                className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center active:bg-white/20"
              >
                <Move3D size={20} />
              </button>
            </div>

            <p className="text-center text-white/40 text-xs">
              Use touch to orbit • Pinch to zoom
            </p>
          </div>
        </div>
      )}

      {/* Enter VR/AR Buttons */}
      {!isLoading && !isInVR && !isInAR && (vrSupported || arSupported) && (
        <div className="absolute bottom-0 left-0 right-0 p-5 pb-8 safe-area-bottom z-20">
          <div className="flex flex-col gap-3">
            {/* AR/MR Button - shows passthrough with model */}
            {arSupported && (
              <Button
                fullWidth
                size="lg"
                onClick={handleEnterAR}
                icon={<Camera size={20} />}
                className="bg-gradient-to-r from-purple-600 to-pink-600"
              >
                Mixed Reality Mode
              </Button>
            )}
            {/* VR Button - full immersive VR environment */}
            {vrSupported && (
              <Button
                fullWidth
                size="lg"
                onClick={handleEnterVR}
                icon={<Glasses size={20} />}
                className="bg-gradient-to-r from-accent-blue to-accent-purple"
              >
                VR Environment Mode
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Fullscreen button for non-VR devices */}
      {!isLoading && !vrSupported && !arSupported && (
        <div className="absolute bottom-0 left-0 right-0 p-5 pb-8 safe-area-bottom z-20">
          <Button
            fullWidth
            size="lg"
            onClick={() => canvasRef.current?.requestFullscreen?.()}
            icon={<Maximize2 size={20} />}
          >
            Fullscreen 3D View
          </Button>
        </div>
      )}

      {/* Exit VR/AR Button (shown when in XR session) */}
      {(isInVR || isInAR) && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
          <button
            onClick={handleExitVR}
            className="px-6 py-3 rounded-full glass-dark flex items-center gap-2"
          >
            <X size={18} />
            {isInAR ? 'Exit MR' : 'Exit VR'}
          </button>
        </div>
      )}

      {/* Help Modal */}
      <AnimatePresence>
        {showHelp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/80 z-30 flex items-center justify-center p-6"
            onClick={() => setShowHelp(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass rounded-3xl p-6 max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">VR Controls</h2>
                <button
                  onClick={() => setShowHelp(false)}
                  className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Hand Tracking Section */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Hand size={18} className="text-accent-blue" />
                  <h3 className="font-semibold">Hand Tracking</h3>
                </div>
                <ul className="space-y-2 text-sm text-white/70">
                  <li className="flex items-start gap-2">
                    <span className="text-accent-blue">•</span>
                    <span><strong>Pinch</strong> (thumb + index) near model to grab</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-blue">•</span>
                    <span><strong>Move hand</strong> while pinching to reposition</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-blue">•</span>
                    <span><strong>Two-hand pinch</strong> to scale and rotate</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-blue">•</span>
                    <span><strong>Release pinch</strong> to drop model</span>
                  </li>
                </ul>
              </div>

              {/* Controller Section */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Gamepad2 size={18} className="text-accent-purple" />
                  <h3 className="font-semibold">Controllers</h3>
                </div>
                <ul className="space-y-2 text-sm text-white/70">
                  <li className="flex items-start gap-2">
                    <span className="text-accent-purple">•</span>
                    <span><strong>Left Stick</strong> to move around</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-purple">•</span>
                    <span><strong>Right Stick</strong> to turn view / rotate model</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-purple">•</span>
                    <span><strong>Trigger</strong> to grab model</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-purple">•</span>
                    <span><strong>Grip</strong> to reset model position</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-purple">•</span>
                    <span><strong>A/X</strong> scale up • <strong>B/Y</strong> scale down</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-purple">•</span>
                    <span><strong>Menu button</strong> to exit VR</span>
                  </li>
                </ul>
              </div>

              <Button fullWidth onClick={() => setShowHelp(false)}>
                Got it!
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
